import React, { useCallback, useState } from "react";
import { Loader2, Send } from "lucide-react";

type MessageComposerProps = {
  onSubmit: (message: string) => Promise<boolean> | boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
};

const MessageComposer: React.FC<MessageComposerProps> = ({
  onSubmit,
  disabled,
  loading,
  error,
  placeholder = "Type your message",
}) => {
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSend = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || loading) return false;
    setLocalError(null);
    try {
      const result = await onSubmit(trimmed);
      if (result !== false) {
        setValue("");
      }
      return result !== false;
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to send message");
      return false;
    }
  }, [disabled, loading, onSubmit, value]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await handleSend();
    },
    [handleSend],
  );

  const handleKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        await handleSend();
      }
    },
    [handleSend],
  );

  const isSubmitDisabled = disabled || loading || !value.trim();

  const toolbarButtonStyle: React.CSSProperties = {
    height: "36px",
    width: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "none",
    cursor: "not-allowed",
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: "20px 24px",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "12px",
          backgroundColor: "#f8fafc",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          padding: "12px",
        }}
      >
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          disabled={disabled || loading}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            resize: "none",
            background: "transparent",
            fontSize: "0.95rem",
            lineHeight: 1.5,
            color: "#0f172a",
          }}
        />
        <button
          type="submit"
          disabled={isSubmitDisabled}
          style={{
            border: "none",
            borderRadius: "12px",
            padding: "10px 16px",
            backgroundColor: isSubmitDisabled ? "#cbd5f5" : "#2563eb",
            color: "#ffffff",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: isSubmitDisabled ? "not-allowed" : "pointer",
            boxShadow: isSubmitDisabled ? "none" : "0 12px 24px rgba(37, 99, 235, 0.35)",
          }}
        >
          {loading ? <Loader2 style={{ height: "18px", width: "18px" }} /> : <Send style={{ height: "18px", width: "18px" }} />}
          <span>Send</span>
        </button>
      </div>
      {(error || localError) && (
        <p style={{ margin: 0, color: "#dc2626", fontSize: "0.85rem" }}>{error || localError}</p>
      )}
      <p style={{ margin: 0, color: "#94a3b8", fontSize: "0.75rem" }}>
        Press Enter to send, Shift + Enter for a new line.
      </p>
    </form>
  );
};

export default MessageComposer;
